<script setup>
import { can } from '@/@layouts/plugins/casl'
import { userAuthState } from '@core/auth'
import { RcaStatus } from '@/enums/quality/rca.status'

// Context components
import IncidentContext from '@/views/quality/rca/context/IncidentContext.vue'
import KpiEvaluationContext from '@/views/quality/rca/context/KpiEvaluationContext.vue'
import OutbreakContext from '@/views/quality/rca/context/OutbreakContext.vue'
import RiskContext from '@/views/quality/rca/context/RiskContext.vue'

// Section components
import InvestigatorPanel from '@/views/quality/rca/sections/InvestigatorPanel.vue'
import GeneralSection from '@/views/quality/rca/sections/GeneralSection.vue'
import FiveWhySection from '@/views/quality/rca/sections/FiveWhySection.vue'
import FishboneSection from '@/views/quality/rca/sections/FishboneSection.vue'
import CapaSection from '@/views/quality/rca/sections/CapaSection.vue'
import RiskAssessmentSection from '@/views/quality/rca/sections/RiskAssessmentSection.vue'
import AttachmentSection from '@/views/quality/rca/sections/AttachmentSection.vue'

import StrategiesCard from '@/views/quality/rca/sections/StrategiesCard.vue'

// Page shell
import RcaHeader from '@/views/quality/rca/views/RcaHeader.vue'
import RcaProgress from '@/views/quality/rca/views/RcaProgress.vue'

// Sidebar components
import VerificationCard from '@/views/quality/rca/sidebar/VerificationCard.vue'
import RatingsCard from '@/views/quality/rca/sidebar/RatingsCard.vue'
import ActivityCard from '@/views/quality/rca/sidebar/ActivityCard.vue'

// Investigation drawers
import AiInsightService from '@/services/quality/ai-insight.service'
import AiInsightCard from '@/views/quality/incident-panel/ai/AiInsightCard.vue'
import GeneralDrawer from '@/views/quality/rca/investigation/GeneralDrawer.vue'
import FiveWhyDrawer from '@/views/quality/rca/investigation/FiveWhyDrawer.vue'
import FishboneDrawer from '@/views/quality/rca/investigation/FishboneDrawer.vue'

// Promote to Risk
import RiskPromoteDialog from '@/views/quality/risk/RiskPromoteDialog.vue'

definePage({
  meta: {
    name: 'incident-rca-view',
    subject: 'quality.incident_rca',
    action: 'read',
  },
})

const route = useRoute()
const router = useRouter()
const auth = userAuthState()

const rca = ref()

const { execute: refetchRca } = await useApi(
  createUrl(`/incidents/incident-rcas/${route.params.id}`),
  {
    afterFetch: ({ data }) => { rca.value = data },
    onFetchError: () => {
      router.push({ name: 'invalid-url', params: { code: 404 } })
    },
  },
)

// Subject type helper
const subjectType = computed(() => {
  const type = rca.value?.subject_type
  if (!type) return null

  return type.split('\\').pop()
})

const subject = computed(() => rca.value?.subject)

// Strategy helpers
const generalRcaStrategy = computed(() =>
  rca.value?.incident_rca_strategies?.find(s => s.rca_strategy === 'GENERAL'),
)

// Auth helpers
const authUserIsDelegate = computed(() => {
  let isDelegate = rca.value?.user_delegates?.findIndex(d => d.delegate?.id === auth?.user?.id) > -1
  if (!isDelegate) {
    isDelegate = rca.value?.group_delegates?.findIndex(
      g => g.delegate?.users?.findIndex(u => u.id === auth?.user?.id) > -1,
    ) > -1
  }

  return isDelegate
})

const authUserIsVerifier = computed(() =>
  rca.value?.verifiers?.findIndex(v => v.id === auth?.user?.id) > -1,
)

// Permission helpers
const canEditRca = computed(() =>
  can('perform_rca', 'quality.incident_rca')
  && [RcaStatus.PENDING, RcaStatus.DRAFT, RcaStatus.RE_OPENED].includes(rca.value?.rca_status)
  && authUserIsDelegate.value,
)

const canSendForVerification = computed(() =>
  can('perform_rca', 'quality.incident_rca')
  && rca.value?.rca_status === RcaStatus.DRAFT
  && (!rca.value?.risk_assessment_status || rca.value?.risk_priority_no)
  && generalRcaStrategy.value
  && authUserIsDelegate.value,
)

const canVerify = computed(() =>
  can('verification', 'quality.incident_rca')
  && rca.value?.rca_status === RcaStatus.COMPLETED
  && authUserIsVerifier.value,
)

// Drawer state
const isGeneralDrawerVisible = ref(false)
const isFiveWhyDrawerVisible = ref(false)
const isFishboneDrawerVisible = ref(false)

// RCA update callback
const onRcaUpdate = data => {
  if (!data) return
  Object.assign(rca.value, data)
}

// ── AI investigation assist ───────────────────────────────────────────────
const investigationInsight = computed(() =>
  (rca.value?.ai_insights ?? []).find(i => i.kind === 'investigation') ?? null)

const reviewInsight = computed(() =>
  (rca.value?.ai_insights ?? []).find(i => i.kind === 'review') ?? null)

// Verifiers while the RCA awaits verification; department reviewers while it awaits review. Retry is the only action.
const canActOnReview = computed(() =>
  (authUserIsVerifier.value && rca.value?.rca_status === RcaStatus.COMPLETED)
  || (can('review', 'quality.incident_rca') && rca.value?.rca_status === RcaStatus.VERIFIED))

const onInsightUpdate = fresh => {
  const list = [...(rca.value?.ai_insights ?? [])]
  const idx = list.findIndex(i => i.id === fresh.id)

  if (idx === -1) list.unshift(fresh)
  else list.splice(idx, 1, fresh)
  rca.value = { ...rca.value, ai_insights: list }
}

const stampApplied = async (insightId, keys) => {
  for (const key of keys) {
    try {
      onInsightUpdate(await AiInsightService.setItemState(insightId, key, 'applied'))
    } catch {
      // toast shown by the service; the save itself already succeeded
    }
  }
}

// Factor pairs / brief → General Investigation drawer
const generalInitialValues = ref(null)
const pendingFactorApply = ref(null)
const pendingBriefApply = ref(null)

// "Use" while that drawer is already open: close it and wait a tick so the open watcher fires again.
const reopen = async flag => {
  if (!flag.value) return
  flag.value = false
  await nextTick()
}

const onUseInvestigation = async ({ insightId, factors, brief }) => {
  await reopen(isGeneralDrawerVisible)
  generalInitialValues.value = { factors, brief: brief || '' }
  pendingFactorApply.value = { insightId, keys: factors.map(f => f.key) }
  pendingBriefApply.value = brief ? { insightId, key: 'brief', text: brief } : null
  isGeneralDrawerVisible.value = true
}

const savedPairKeys = data => {
  const strategy = data?.incident_rca_strategies?.find(s => s.rca_strategy === 'GENERAL')
  const groups = [...(strategy?.broad_contributory_factors ?? []), ...(strategy?.specific_contributory_factors ?? [])]

  return new Set(groups.flatMap(g => (g.child_factors ?? []).map(c => `factor:${c.factor}:${c.child_factor_id}`)))
}

const onGeneralSaved = async data => {
  onRcaUpdate(data)

  const pendingFactors = pendingFactorApply.value
  const pendingBrief = pendingBriefApply.value

  pendingFactorApply.value = null
  pendingBriefApply.value = null

  if (pendingFactors) {
    const saved = savedPairKeys(data)

    await stampApplied(pendingFactors.insightId, pendingFactors.keys.filter(k => saved.has(k)))
  }

  const savedBrief = (data?.incident_rca_strategies?.find(s => s.rca_strategy === 'GENERAL')?.brief_of_investigation ?? '').trim()
  const draftHead = (pendingBrief?.text ?? '').trim().slice(0, 60)

  if (pendingBrief && draftHead && savedBrief.includes(draftHead)) await stampApplied(pendingBrief.insightId, [pendingBrief.key])
}

watch(isGeneralDrawerVisible, open => {
  if (!open) {
    generalInitialValues.value = null
    pendingFactorApply.value = null
    pendingBriefApply.value = null
  }
})

// Five Whys draft → Five Why drawer
const fiveWhyInitialValues = ref(null)
const pendingFiveWhyApply = ref(null)

const onUseFiveWhys = async ({ insightId, key, steps, brief }) => {
  await reopen(isFiveWhyDrawerVisible)
  fiveWhyInitialValues.value = { steps, brief }
  pendingFiveWhyApply.value = { insightId, key, steps }
  isFiveWhyDrawerVisible.value = true
}

const onFiveWhySaved = async data => {
  onRcaUpdate(data)

  const pending = pendingFiveWhyApply.value

  pendingFiveWhyApply.value = null
  if (!pending) return

  const saved = data?.incident_rca_strategies?.find(s => s.rca_strategy === 'FIVE_WHYS')?.five_whys ?? []
  const savedQuestions = new Set(saved.filter(s => s.question && s.answer).map(s => s.question.trim()))
  const kept = pending.steps.filter(s => savedQuestions.has((s.question ?? '').trim())).length

  if (kept >= 3) await stampApplied(pending.insightId, [pending.key])
}

watch(isFiveWhyDrawerVisible, open => {
  if (!open) {
    fiveWhyInitialValues.value = null
    pendingFiveWhyApply.value = null
  }
})

// Fishbone draft → Fishbone drawer
const fishboneInitialValues = ref(null)
const pendingFishboneApply = ref(null)

const onUseFishbone = async ({ insightId, keys, entries, brief }) => {
  await reopen(isFishboneDrawerVisible)
  fishboneInitialValues.value = { entries, brief }
  pendingFishboneApply.value = { insightId, keys, entries }
  isFishboneDrawerVisible.value = true
}

const onFishboneSaved = async data => {
  onRcaUpdate(data)

  const pending = pendingFishboneApply.value

  pendingFishboneApply.value = null
  if (!pending) return
  const saved = data?.incident_rca_strategies?.find(s => s.rca_strategy === 'FISHBONE')?.fishbone ?? []
  const savedCategoryIds = new Set(saved.filter(f => (f.reasons ?? []).length).map(f => Number(f.fishbone_category_id)))
  const keys = pending.entries.filter(e => savedCategoryIds.has(Number(e.category_id))).map(e => `fishbone:${e.category_id}`)

  await stampApplied(pending.insightId, keys)
}

watch(isFishboneDrawerVisible, open => {
  if (!open) {
    fishboneInitialValues.value = null
    pendingFishboneApply.value = null
  }
})

// CAPA suggestion → CAPA drawer
const capaInitialValues = ref(null)
const pendingCapaApply = ref(null)

const onUseCapa = ({ insightId, key, factor_id, factor_label, corrective_action, preventive_action }) => {
  capaInitialValues.value = {
    contributory_factor: { id: factor_id, name: factor_label },
    corrective_action,
    preventive_action,
  }
  pendingCapaApply.value = { insightId, key, factor_id }
}

const onCapaSaved = async capa => {
  // Merge the saved CAPA into the page's RCA so the AI "Use" guard sees it straight away.
  const capas = [...(rca.value?.incident_capas ?? [])]
  const idx = capas.findIndex(c => Number(c.id) === Number(capa?.id))

  if (capa?.id) {
    if (idx === -1) capas.push(capa)
    else capas.splice(idx, 1, capa)
  }
  rca.value = { ...rca.value, incident_capas: capas }

  const pending = pendingCapaApply.value

  pendingCapaApply.value = null
  if (!pending || pending.factor_id == null || capa?.contributory_factor_id == null) return
  if (Number(capa.contributory_factor_id) !== Number(pending.factor_id)) return
  await stampApplied(pending.insightId, [pending.key])
}

watch(capaInitialValues, value => {
  if (!value) pendingCapaApply.value = null
})

// Promote to Risk
const { isOpen: isPromoteDialogOpen, source: promoteSource, promptPromote } = useRiskPromotion()

const canPromote = computed(() => can('promote', 'quality.risk'))

const openPromoteDialog = () => {
  promptPromote('INCIDENT_RCA', rca.value.id, rca.value.unit_id, {
    title: rca.value?.title ?? '',
    description: generalRcaStrategy.value?.brief_of_investigation ?? '',
  })
}

const onPromoted = risk => {
  router.push({ name: 'app-quality-risk-id', params: { id: risk.id } })
}

// Desktop: whichever column ends first pins to the viewport while the other keeps scrolling.
// A column taller than the viewport pins once its end comes into view instead of leaving a gap.
const leftColumn = ref(null)
const rightColumn = ref(null)

const PANE_TOP = 88
const COLUMN_GAP = 16

let resizeObserver = null

const updateStickyColumns = () => {
  const wide = window.matchMedia('(min-width: 1100px)').matches

  for (const el of [leftColumn.value, rightColumn.value]) {
    if (!el) continue
    if (!wide) {
      el.style.removeProperty('--col-top')
      continue
    }

    const top = Math.min(PANE_TOP, window.innerHeight - el.offsetHeight - COLUMN_GAP)

    el.style.setProperty('--col-top', `${top}px`)
  }
}

onMounted(() => {
  resizeObserver = new ResizeObserver(updateStickyColumns)
  for (const el of [leftColumn.value, rightColumn.value]) {
    if (el) resizeObserver.observe(el)
  }
  window.addEventListener('resize', updateStickyColumns)
  updateStickyColumns()
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  window.removeEventListener('resize', updateStickyColumns)
})
</script>

<template>
  <div
    v-if="rca"
    class="quality-view rca-view"
  >
    <div class="d-flex flex-column gap-4">
      <RcaHeader
        :rca="rca"
        :can-promote="canPromote"
        @promote="openPromoteDialog"
      />

      <RcaProgress :rca="rca" />

      <div class="rca-cols">
        <!-- Main column -->
        <div
          ref="leftColumn"
          class="rca-col"
        >
          <!-- Source Context -->
          <IncidentContext
            v-if="subjectType === 'Incident'"
            :incident="subject"
            :rca="rca"
          />
          <KpiEvaluationContext
            v-else-if="subjectType === 'KPIEvaluation'"
            :evaluation="subject"
          />
          <OutbreakContext
            v-else-if="subjectType === 'Outbreak'"
            :outbreak="subject"
          />
          <RiskContext
            v-else-if="subjectType === 'Risk'"
            :risk="subject"
          />
          <VCard
            v-else
            variant="flat"
            class="border"
          >
            <VCardText class="text-center py-6 text-medium-emphasis">
              No context view available for subject type "{{ subjectType ?? 'Unknown' }}".
            </VCardText>
          </VCard>

          <InvestigatorPanel :rca="rca" />

          <AiInsightCard
            v-if="investigationInsight"
            :insight="investigationInsight"
            :rca="rca"
            :can-act="canEditRca"
            :capa-enabled="!!generalRcaStrategy"
            aura
            @update:insight="onInsightUpdate"
            @use-investigation="onUseInvestigation"
            @use-five-whys="onUseFiveWhys"
            @use-fishbone="onUseFishbone"
            @use-capa="onUseCapa"
          />

          <AiInsightCard
            v-if="reviewInsight"
            :insight="reviewInsight"
            :rca="rca"
            :can-act="canActOnReview"
            @update:insight="onInsightUpdate"
          />

          <StrategiesCard
            :rca="rca"
            :can-edit="canEditRca"
          >
            <GeneralSection
              :rca="rca"
              :can-edit="canEditRca"
              :auth-user-is-delegate="authUserIsDelegate"
              @update:rca="onRcaUpdate"
              @open-drawer="isGeneralDrawerVisible = true"
            />

            <FiveWhySection
              :rca="rca"
              :can-edit="canEditRca"
              :disabled="!generalRcaStrategy"
              :auth-user-is-delegate="authUserIsDelegate"
              :general-rca-strategy="generalRcaStrategy"
              @update:rca="onRcaUpdate"
              @open-drawer="isFiveWhyDrawerVisible = true"
            />

            <FishboneSection
              :rca="rca"
              :can-edit="canEditRca"
              :disabled="!generalRcaStrategy"
              :auth-user-is-delegate="authUserIsDelegate"
              :general-rca-strategy="generalRcaStrategy"
              @update:rca="onRcaUpdate"
              @open-drawer="isFishboneDrawerVisible = true"
            />
          </StrategiesCard>

          <CapaSection
            v-model:initial-capa="capaInitialValues"
            :rca="rca"
            :can-edit="canEditRca"
            :disabled="!generalRcaStrategy"
            :auth-user-is-delegate="authUserIsDelegate"
            :general-rca-strategy="generalRcaStrategy"
            @update:rca="onRcaUpdate"
            @capa-saved="onCapaSaved"
          />

          <RiskAssessmentSection
            :rca="rca"
            :can-edit="canEditRca"
            :auth-user-is-delegate="authUserIsDelegate"
            :capa-count="rca.incident_capas?.length ?? 0"
            @update:rca="onRcaUpdate"
          />

          <AttachmentSection :rca="rca" />
        </div>

        <!-- Side column -->
        <div
          ref="rightColumn"
          class="rca-col"
        >
          <VerificationCard
            :rca="rca"
            :can-send="canSendForVerification"
            :can-verify="canVerify"
            @update:rca="onRcaUpdate"
          />
          <RatingsCard :rca="rca" />
          <ActivityCard :rca="rca" />
        </div>
      </div>
    </div>

    <!-- Drawers -->
    <GeneralDrawer
      v-model:isDrawerOpen="isGeneralDrawerVisible"
      :rca="rca"
      :initial-values="generalInitialValues"
      @update:rca="onGeneralSaved"
    />
    <FiveWhyDrawer
      v-model:isDrawerOpen="isFiveWhyDrawerVisible"
      :rca="rca"
      :initial-values="fiveWhyInitialValues"
      @update:rca="onFiveWhySaved"
    />
    <FishboneDrawer
      v-model:isDrawerOpen="isFishboneDrawerVisible"
      :rca="rca"
      :initial-values="fishboneInitialValues"
      @update:rca="onFishboneSaved"
    />

    <RiskPromoteDialog
      v-model:is-dialog-open="isPromoteDialogOpen"
      :source-type="promoteSource.type"
      :source-id="promoteSource.id"
      :source-unit-id="promoteSource.unitId"
      :prefill="promoteSource.prefill"
      @promoted="onPromoted"
    />
  </div>
</template>

<style lang="scss">
@use "@styles/quality/panel-view";

/* Drawers teleported out of the sticky columns land in these hosts under <body>.
   Vuetify positions the scrim absolutely, which from <body> only covers the first
   screen, so pin it to the viewport. */
.rca-drawer-host .v-navigation-drawer__scrim {
  position: fixed;
  inset: 0;
}
</style>

<style scoped>
/* The KPI, outbreak and risk context cards still use AppCardActions: tighten their
   header and body to the reference's card chrome and box the collapse chevron like
   the strategy rows. The incident card draws its own header. */
.rca-view :deep(.v-card-item) {
  padding: 10px 16px;
}

.rca-view :deep(.v-card-content .v-card-text) {
  padding: 12px 16px;
}

.rca-view :deep(.v-card-item .v-card-item__append .v-btn--icon) {
  inline-size: 36px;
  block-size: 32px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
}

.rca-cols {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 400px;
  gap: 16px;
  align-items: start;
}

.rca-col {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-inline-size: 0;
}

@media (min-width: 1100px) {
  .rca-col {
    position: sticky;
    inset-block-start: var(--col-top, 88px);
  }
}

@media (max-width: 1099px) {
  .rca-cols {
    grid-template-columns: 1fr;
  }
}

/* Phones: a card header whose append holds a pill and a button wraps the
   actions under the title instead of squeezing the title word by word. */
@media (max-width: 599px) {
  .rca-view :deep(.panel-card__header) {
    flex-wrap: wrap;
  }

  .rca-view :deep(.panel-card__title) {
    flex: 1 1 auto;
  }
}
</style>
